import styled from 'styled-components';

export const DocumentGridContainer = styled.div<{ $isCompact?: boolean }>`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${({ $isCompact }) => ($isCompact ? '220px' : '280px')}, 1fr)
  );
  gap: ${({ $isCompact }) => ($isCompact ? '0.85rem' : '1.5rem')};
  margin-bottom: ${({ $isCompact }) => ($isCompact ? '1.25rem' : '2rem')};
`;

export const DocumentCard = styled.div<{ $isCompact?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.border.light || '#e0e0e0'};
  border-radius: ${({ $isCompact, theme }) =>
    $isCompact ? theme.borderRadius.sm : '8px'};
  padding: ${({ $isCompact }) => ($isCompact ? '0.8rem' : '1rem')};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease,
    background 0.15s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    ${({ $isCompact, theme }) =>
      $isCompact
        ? `
      border-color: ${theme.colors.primary};
      box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
      background: ${theme.colors.primary}06;
    `
        : `
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    `}
  }
`;

export const DocumentCardHeader = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ $isCompact }) => ($isCompact ? '0.45rem' : '0.75rem')};
`;

export const DocumentIcon = styled.div<{ $isCompact?: boolean }>`
  font-size: ${({ $isCompact }) => ($isCompact ? '1.4rem' : '2rem')};
  text-align: center;
  margin-bottom: ${({ $isCompact }) => ($isCompact ? '0' : '0.5rem')};
`;

export const DocumentActions = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
  position: relative;

  ${DocumentCard}:hover & {
    opacity: 1;
  }

  ${DocumentCard}:focus-within & {
    opacity: 1;
  }
`;

export const ActionButton = styled.button<{ $isCompact?: boolean }>`
  background: transparent;
  border: none;
  padding: ${({ $isCompact }) =>
    $isCompact ? '0.2rem 0.35rem' : '0.25rem 0.5rem'};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ $isCompact }) => ($isCompact ? '0.85rem' : '1rem')};
  transition: all 0.15s ease;
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }

  ${({ $isCompact, theme }) =>
    $isCompact
      ? `
    color: ${theme.colors.text.secondary};

    &:hover {
      background: ${theme.colors.background || '#f5f5f5'};
      color: ${theme.colors.primary};
      border: 1px solid ${theme.colors.border.light || '#e0e0e0'};
    }

    &:active {
      transform: scale(0.95);
    }
  `
      : `
    &:hover {
      background: ${theme.colors.background || '#f5f5f5'};
      border: 1px solid ${theme.colors.border.light || '#e0e0e0'};
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.95);
    }
  `}
`;

export const DocumentTitle = styled.h3<{ $isCompact?: boolean }>`
  font-size: ${({ $isCompact }) => ($isCompact ? '0.88rem' : '0.95rem')};
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: ${({ theme }) => theme.colors.text.primary || '#333'};
  line-height: 1.3;
  overflow: hidden;
`;

export const DocumentSynopsis = styled.div<{ $isCompact?: boolean }>`
  font-size: 0.75rem;
  color: ${({ $isCompact, theme }) =>
    $isCompact ? theme.colors.text.secondary : '#666'};
  margin: 0.5rem 0;
  line-height: 1.45;

  ${({ $isCompact }) =>
    $isCompact
      ? `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `
      : ''}
`;

export const DocumentMeta = styled.div<{ $isCompact?: boolean }>`
  font-size: ${({ $isCompact }) => ($isCompact ? '0.72rem' : '0.75rem')};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ $isCompact }) => ($isCompact ? '0.35rem' : '0.5rem')};
  line-height: 1.4;
`;

export const DocumentTags = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  gap: ${({ $isCompact }) => ($isCompact ? '0.35rem' : '0.5rem')};
  flex-wrap: wrap;
  margin-top: ${({ $isCompact }) => ($isCompact ? '0.45rem' : '0.75rem')};
`;

export const Tag = styled.span<{
  variant?: 'primary' | 'secondary' | 'warning';
}>`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  background: ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return '#e3f2fd';
      case 'warning':
        return '#fff3e0';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return '#1976d2';
      case 'warning':
        return '#f57c00';
      default:
        return '#666';
    }
  }};
`;

export const DocumentTypeLabel = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.5rem;
  font-weight: 500;
`;

export const DocumentVersionLabel = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.25rem;
`;

export const DocumentFormatLabel = styled.div`
  font-size: 0.75rem;
  color: #999;
  margin-top: 0.25rem;
  text-transform: uppercase;
`;

export const LinkedBadge = styled(Tag)<{ variant?: 'primary' | 'secondary' | 'warning' | 'info' }>`
  background: ${({ variant }) => {
    switch (variant) {
      case 'info':
        return '#e1f5fe';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${({ variant }) => {
    switch (variant) {
      case 'info':
        return '#0277bd';
      default:
        return '#666';
    }
  }};
  cursor: help;
  position: relative;
`;

export const LinkedIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  margin-top: 0.5rem;
  color: #0277bd;
  font-weight: 500;
`;


